import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

import { axiosInstance } from '../helpers'
import { MessagingConfig } from '../models/Concession'
import { useUser } from './useUser'

const CONFIG_KEY = 'messaging-config'
const BASE = '/beta/api/concession/messaging-config'

const extractError = (error: unknown, fallback: string): string => {
    const data = (error as AxiosError)?.response?.data as
        | Record<string, unknown>
        | undefined

    if (data) {
        const firstKey = Object.keys(data)[0]
        if (firstKey) {
            const val = data[firstKey]
            if (Array.isArray(val) && val.length > 0) {
                return `${firstKey}: ${val[0]}`
            }
            if (typeof val === 'string') return val
        }
    }
    return fallback
}

export const useMessagingConfig = () => {
    const queryClient = useQueryClient()
    const { workspaceUser } = useUser()

    const apartmentId = workspaceUser?.active_workspace?.id
    const isApartment =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'

    const { data: config, isLoading, refetch } = useQuery<
        MessagingConfig,
        Error
    >({
        queryKey: [CONFIG_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<MessagingConfig>(`${BASE}/`)
            return res.data
        },
        enabled: isApartment && !!apartmentId,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const updateConfig = useMutation({
        mutationFn: async (body: Partial<MessagingConfig>) => {
            const res = await axiosInstance.put<MessagingConfig>(
                `${BASE}/`,
                body,
            )
            return res.data
        },
        onSuccess: (data) => {
            queryClient.setQueryData([CONFIG_KEY, apartmentId], data)
            toast.success('Settings saved')
        },
        onError: (err) => {
            toast.error(extractError(err, 'Failed to save settings'))
        },
    })

    return {
        config: config ?? null,
        isLoading,
        refetch,
        updateConfig,
    }
}
